/**
 * The React side of the studio chrome.
 *
 * Kept in its own module because importing `#/alf` drags in the whole atom and
 * dialog tree, which a pure unit test of the token table neither needs nor can
 * load. `#/themes-studio/chrome` holds the tokens; this holds the hook that
 * reads the app's current appearance.
 */
import {useMemo} from 'react'

import {useTheme} from '#/alf'
import {buildStudioChrome, type StudioChrome} from '#/themes-studio/chrome'

export {buildStudioChrome, type StudioChrome} from '#/themes-studio/chrome'

/** The chrome for the appearance the app is currently in. */
export function useStudioChrome(): StudioChrome {
  const theme = useTheme()
  return useMemo(() => buildStudioChrome(theme), [theme])
}

/**
 * Build a `StyleSheet` (or any style bag) from the chrome, memoised on the
 * appearance so a re-render in the same appearance hands back the same object.
 *
 * `factory` must be defined at module scope — a fresh closure each render would
 * rebuild the styles every render and defeat the memo.
 */
export function useStudioStyles<T>(factory: (chrome: StudioChrome) => T): T {
  const chrome = useStudioChrome()
  return useMemo(() => factory(chrome), [chrome, factory])
}
